import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const MOOD_SCORES = {
  joyeux: 5,
  excite: 5,
  calme: 4,
  fatigue: 2,
  triste: 1,
  colere: 1,
};

const MOOD_LABEL = {
  1: "😢",
  2: "😴",
  3: "😐",
  4: "😌",
  5: "😊",
};

export default function MoodChart({ entries }) {
  const data = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      d.setHours(0, 0, 0, 0);
      days.push(d);
    }
    return days.map((d) => {
      const dayEntries = entries.filter((e) => {
        const ed = new Date(e.created_at);
        return ed.toDateString() === d.toDateString();
      });
      const avg = (key) => {
        const vals = dayEntries.map((e) => MOOD_SCORES[e[key]] || 3);
        return vals.length ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
      };
      return {
        label: d.toLocaleDateString("fr-FR", { weekday: "short" }),
        parent: avg("parent_mood"),
        enfant: avg("child_mood"),
      };
    });
  }, [entries]);

  const hasData = data.some((d) => d.parent !== null || d.enfant !== null);

  if (!hasData) {
    return (
      <div className="soft-card p-6 text-center text-[#77849C]" data-testid="mood-chart-empty">
        Ajoute quelques entrées journal pour voir l'évolution de l'humeur cette semaine 💛
      </div>
    );
  }

  return (
    <div className="soft-card p-6" data-testid="mood-chart">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]">Analyse humeur</p>
          <h3 className="text-xl font-semibold text-[#16233D]">7 derniers jours</h3>
        </div>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E3E8F1" />
            <XAxis dataKey="label" stroke="#77849C" fontSize={12} />
            <YAxis
              domain={[1, 5]}
              ticks={[1, 2, 3, 4, 5]}
              tickFormatter={(v) => MOOD_LABEL[v] || v}
              stroke="#77849C"
              fontSize={16}
            />
            <Tooltip
              contentStyle={{
                background: "#FFFFFF",
                border: "1px solid #E3E8F1",
                borderRadius: "12px",
                fontSize: "13px",
              }}
              formatter={(val) => (val === null ? "—" : val)}
            />
            <Legend wrapperStyle={{ fontSize: 13 }} />
            <Line
              type="monotone"
              dataKey="parent"
              name="Parent"
              stroke="#23375E"
              strokeWidth={3}
              dot={{ r: 5, fill: "#23375E" }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="enfant"
              name="Enfant"
              stroke="#E8899F"
              strokeWidth={3}
              dot={{ r: 5, fill: "#E8899F" }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
